import type {
  MeasurementQualifierViewModel,
  MeasurementType,
} from '@/features/patient-record/types';

/**
 * Conversion helpers for measurement values. Storage stays **metric**
 * (kg, cm) — units only decide how a value is entered and how a row
 * renders, never what hits the API.
 */

export const UNIT_SYSTEM = {
  metric: 'metric',
  imperial: 'imperial',
} as const;
export type UnitSystem = (typeof UNIT_SYSTEM)[keyof typeof UNIT_SYSTEM];

/**
 * The units a value can be entered in. `ft_in` is the split feet + inches
 * pair — the way heights are actually quoted — and is the one unit that
 * carries two numbers; every other unit is a single number.
 */
export const MEASUREMENT_UNIT = {
  kg: 'kg',
  lbs: 'lbs',
  cm: 'cm',
  m: 'm',
  ftIn: 'ft_in',
  inch: 'in',
} as const;
export type MeasurementUnit =
  (typeof MEASUREMENT_UNIT)[keyof typeof MEASUREMENT_UNIT];

const KG_PER_LB = 0.453592;
const CM_PER_IN = 2.54;
const IN_PER_FT = 12;

const WEIGHT_UNITS: readonly MeasurementUnit[] = [
  MEASUREMENT_UNIT.kg,
  MEASUREMENT_UNIT.lbs,
];
/** Height and arm span: body lengths quoted in feet/inches or metres. */
const STATURE_UNITS: readonly MeasurementUnit[] = [
  MEASUREMENT_UNIT.cm,
  MEASUREMENT_UNIT.m,
  MEASUREMENT_UNIT.ftIn,
  MEASUREMENT_UNIT.inch,
];
/** Head circumference is never quoted in feet or metres. */
const GIRTH_UNITS: readonly MeasurementUnit[] = [
  MEASUREMENT_UNIT.cm,
  MEASUREMENT_UNIT.inch,
];

export function unitOptionsFor(type: MeasurementType): readonly MeasurementUnit[] {
  if (type === 'WEIGHT') return WEIGHT_UNITS;
  if (type === 'HEAD_CIRCUMFERENCE') return GIRTH_UNITS;
  return STATURE_UNITS;
}

/** The unit a field starts in for the given card-level metric/imperial toggle. */
export function defaultUnitFor(
  type: MeasurementType,
  system: UnitSystem,
): MeasurementUnit {
  if (type === 'WEIGHT') {
    return system === UNIT_SYSTEM.metric ? MEASUREMENT_UNIT.kg : MEASUREMENT_UNIT.lbs;
  }
  if (system === UNIT_SYSTEM.metric) return MEASUREMENT_UNIT.cm;
  return type === 'HEAD_CIRCUMFERENCE'
    ? MEASUREMENT_UNIT.inch
    : MEASUREMENT_UNIT.ftIn;
}

/** Symbol shown next to a value and in the unit picker. */
export function unitLabel(unit: MeasurementUnit): string {
  return unit === MEASUREMENT_UNIT.ftIn ? 'ft + in' : unit;
}

/** The two numbers a unit carries. `inches` is 0 for every unit but `ft_in`. */
export interface UnitParts {
  readonly primary: number;
  readonly inches: number;
}

/** Entered parts → the stored metric value (kg for weight, cm for lengths). */
export function toMetric(
  unit: MeasurementUnit,
  primary: number,
  inches = 0,
): number {
  switch (unit) {
    case MEASUREMENT_UNIT.kg:
    case MEASUREMENT_UNIT.cm:
      return primary;
    case MEASUREMENT_UNIT.lbs:
      return primary * KG_PER_LB;
    case MEASUREMENT_UNIT.m:
      return primary * 100;
    case MEASUREMENT_UNIT.inch:
      return primary * CM_PER_IN;
    case MEASUREMENT_UNIT.ftIn:
      return (primary * IN_PER_FT + inches) * CM_PER_IN;
  }
}

/**
 * Stored metric value → the parts to show in that unit. Feet/inches round
 * the inches to one decimal and carry into the next foot, so 71.99 in
 * renders as 6′ 0″ rather than 5′ 12″.
 */
export function fromMetric(unit: MeasurementUnit, metric: number): UnitParts {
  if (unit === MEASUREMENT_UNIT.ftIn) {
    const totalInches = metric / CM_PER_IN;
    const feet = Math.floor(totalInches / IN_PER_FT);
    const inches = roundTo(totalInches - feet * IN_PER_FT, 1);
    return inches >= IN_PER_FT
      ? { primary: feet + 1, inches: 0 }
      : { primary: feet, inches };
  }
  switch (unit) {
    case MEASUREMENT_UNIT.kg:
    case MEASUREMENT_UNIT.cm:
      return { primary: roundTo(metric, 2), inches: 0 };
    case MEASUREMENT_UNIT.lbs:
      return { primary: roundTo(metric / KG_PER_LB, 2), inches: 0 };
    case MEASUREMENT_UNIT.m:
      // 4 dp so a stored value's centimetre precision survives the round trip.
      return { primary: roundTo(metric / 100, 4), inches: 0 };
    case MEASUREMENT_UNIT.inch:
      return { primary: roundTo(metric / CM_PER_IN, 2), inches: 0 };
  }
}

/** A value ready to render: the number, and the unit symbol to mute beside it. */
export interface MeasurementDisplay {
  readonly text: string;
  /** `null` when the text already carries its own symbols (5′ 11.7″). */
  readonly unit: string | null;
}

/** Format a stored (metric) value for the chosen display system. */
export function formatMeasurement(
  value: number,
  type: MeasurementType,
  system: UnitSystem,
): MeasurementDisplay {
  const unit = defaultUnitFor(type, system);
  const parts = fromMetric(unit, value);
  if (unit === MEASUREMENT_UNIT.ftIn) {
    return { text: `${String(parts.primary)}′ ${parts.inches.toFixed(1)}″`, unit: null };
  }
  return { text: parts.primary.toFixed(1), unit: unitLabel(unit) };
}

/** `formatMeasurement` flattened to one string, for summaries and labels. */
export function formatMeasurementText(
  value: number,
  type: MeasurementType,
  system: UnitSystem,
): string {
  const { text, unit } = formatMeasurement(value, type, system);
  return unit === null ? text : `${text} ${unit}`;
}

/**
 * Pick the metric value off a qualifier for the given type. WEIGHT uses
 * `valueKg`; length-style types use `valueCm`. Returns `null` if the
 * relevant slot is empty (BE didn't record that measurement at this visit).
 */
export function valueForType(
  qualifier: MeasurementQualifierViewModel,
  type: MeasurementType,
): number | null {
  if (type === 'WEIGHT') return qualifier.valueKg;
  return qualifier.valueCm;
}

/**
 * Compute BMI from weight (kg) and height (cm). Reported in kg/m² —
 * BMI is unit-system invariant in clinical use, so we don't expose an
 * imperial variant.
 */
export function computeBmi(
  weightKg: number | null,
  heightCm: number | null,
): number | null {
  if (weightKg === null || heightCm === null) return null;
  if (weightKg <= 0 || heightCm <= 0) return null;
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

export function roundTo(value: number, places: number): number {
  const factor = Math.pow(10, places);
  return Math.round(value * factor) / factor;
}
